import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import { Printer, ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatRupee as fmt } from '@/lib/format';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmtDate = (s) => s ? new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

export default function ProjectStatement() {
  const { projectId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/projects/${projectId}/statement`, { withCredentials: true })
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, [projectId]);

  if (loading) return <div className="p-8 text-stone-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading statement…</div>;
  if (!data) return <div className="p-8 text-red-700">Failed to load statement.</div>;

  return (
    <div className="min-h-screen bg-stone-100 py-6 print:bg-white print:py-0">
      {/* Toolbar — hidden when printing */}
      <div className="max-w-4xl mx-auto px-4 mb-4 flex items-center justify-between print:hidden">
        <Link to="/admin/projects" className="inline-flex items-center gap-1 text-sm text-stone-600 hover:text-stone-900">
          <ArrowLeft className="w-4 h-4" /> Back to projects
        </Link>
        <Button data-testid="print-statement-btn" onClick={() => window.print()} className="bg-amber-700 hover:bg-amber-800 text-white">
          <Printer className="w-4 h-4 mr-2" /> Print / Save as PDF
        </Button>
      </div>

      {/* Statement */}
      <div id="statement-sheet" className="max-w-4xl mx-auto bg-white shadow-sm print:shadow-none p-8 md:p-12 text-stone-900" style={{ fontFamily: '"Outfit", system-ui, sans-serif' }}>
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between border-b border-stone-300 pb-6 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" data-testid="brand-name">{data.brand.name || 'Project Statement'}</h1>
            {data.brand.tagline && <p className="text-xs text-stone-500 mt-1">{data.brand.tagline}</p>}
          </div>
          <div className="mt-4 md:mt-0 md:text-right text-sm">
            <div className="text-stone-500 uppercase tracking-widest text-[10px]">Statement date</div>
            <div className="font-medium">{new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
          </div>
        </div>

        {/* Client + project */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 text-sm">
          <div>
            <div className="text-stone-500 uppercase tracking-widest text-[10px] mb-1">Prepared for</div>
            <div className="font-semibold text-base" data-testid="cust-name">{data.customer.name || '—'}</div>
            {data.customer.phone && <div className="text-stone-600">{data.customer.phone}</div>}
            {data.customer.email && <div className="text-stone-600">{data.customer.email}</div>}
            {data.customer.address && <div className="text-stone-600 whitespace-pre-line">{data.customer.address}</div>}
          </div>
          <div>
            <div className="text-stone-500 uppercase tracking-widest text-[10px] mb-1">Project</div>
            <div className="font-semibold text-base">{data.project.project_name}</div>
            <div className="text-stone-500 text-[11px] font-mono">{data.project.project_id}</div>
          </div>
        </div>

        {/* Orders — one block each */}
        {data.orders.map((o) => (
          <div key={o.order_id} className="mb-6" data-testid={`stmt-order-${o.order_id}`}>
            <div className="flex items-baseline justify-between border-b border-stone-200 pb-1 mb-2">
              <h3 className="text-sm font-semibold">Order · {o.order_id}</h3>
              <span className="text-xs text-stone-500">{fmtDate(o.order_date)} · <span className="uppercase tracking-widest text-[10px]">{o.status || 'open'}</span></span>
            </div>

            {/* Products */}
            {o.items.length > 0 && (
              <table className="w-full text-xs mb-3">
                <thead className="bg-stone-50 text-stone-600 uppercase tracking-widest text-[10px]">
                  <tr>
                    <th className="text-left px-2 py-1.5">Item</th>
                    <th className="text-right px-2 py-1.5">Ordered</th>
                    <th className="text-right px-2 py-1.5">Delivered</th>
                    <th className="text-right px-2 py-1.5">Unit price</th>
                    <th className="text-right px-2 py-1.5">Billable</th>
                  </tr>
                </thead>
                <tbody>
                  {o.items.map((it, i) => (
                    <tr key={i} className="border-b border-stone-100">
                      <td className="px-2 py-1.5">
                        <div className="font-medium">{it.product_name}</div>
                        <div className="text-[10px] text-stone-500">{[it.product_brand, it.product_size, it.product_color].filter(Boolean).join(' · ')}</div>
                      </td>
                      <td className="text-right px-2 py-1.5 tabular-nums">{it.quantity_ordered} {it.unit}</td>
                      <td className="text-right px-2 py-1.5 tabular-nums">
                        <span className={it.quantity_pending > 0 ? 'text-amber-700 font-medium' : ''}>
                          {it.quantity_delivered} {it.unit}
                        </span>
                        {it.quantity_pending > 0 && <div className="text-[10px] text-amber-700">{it.quantity_pending} pending</div>}
                      </td>
                      <td className="text-right px-2 py-1.5 tabular-nums">{fmt(it.unit_price)}</td>
                      <td className="text-right px-2 py-1.5 tabular-nums font-medium">
                        {fmt(it.line_total)}
                        {it.quantity_pending > 0 && <div className="text-[10px] font-normal text-stone-400">delivered only</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* Services */}
            {o.services.length > 0 && (
              <table className="w-full text-xs mb-2">
                <thead className="bg-stone-50 text-stone-600 uppercase tracking-widest text-[10px]">
                  <tr>
                    <th className="text-left px-2 py-1.5">Service</th>
                    <th className="text-right px-2 py-1.5">Qty</th>
                    <th className="text-right px-2 py-1.5">Rate</th>
                    <th className="text-right px-2 py-1.5">GST</th>
                    <th className="text-right px-2 py-1.5">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {o.services.map((s, i) => (
                    <tr key={i} className="border-b border-stone-100">
                      <td className="px-2 py-1.5">
                        <div className="font-medium">{s.description}</div>
                        <div className="text-[10px] text-stone-500">{s.category}</div>
                      </td>
                      <td className="text-right px-2 py-1.5 tabular-nums">{s.quantity} {s.unit}</td>
                      <td className="text-right px-2 py-1.5 tabular-nums">{fmt(s.unit_price)}</td>
                      <td className="text-right px-2 py-1.5 tabular-nums text-stone-500">{s.gst_percent}%</td>
                      <td className="text-right px-2 py-1.5 tabular-nums font-medium">{fmt(s.line_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}

        {/* Totals */}
        <div className="mt-8 border-t-2 border-stone-800 pt-4 max-w-md ml-auto text-sm space-y-1.5">
          <div className="flex justify-between"><span className="text-stone-600">Products subtotal</span><span className="tabular-nums" data-testid="products-subtotal">{fmt(data.products_subtotal)}</span></div>
          <div className="flex justify-between"><span className="text-stone-600">Services subtotal</span><span className="tabular-nums" data-testid="services-subtotal">{fmt(data.services_subtotal)}</span></div>
          <div className="flex justify-between border-t border-stone-200 pt-1.5 font-medium"><span>Gross total</span><span className="tabular-nums" data-testid="gross-total">{fmt(data.gross_total)}</span></div>
          {data.discount_subtotal > 0 && (
            <div className="flex justify-between text-amber-800"><span>Less: Discount</span><span className="tabular-nums" data-testid="discount-subtotal">− {fmt(data.discount_subtotal)}</span></div>
          )}
          <div className="flex justify-between border-t border-stone-300 pt-1.5 font-semibold text-base"><span>Net billable</span><span className="tabular-nums" data-testid="net-billable">{fmt(data.net_billable)}</span></div>
        </div>

        {/* Payments received */}
        {data.payments.length > 0 && (
          <div className="mt-8">
            <h3 className="text-sm font-semibold uppercase tracking-widest text-stone-600 border-b border-stone-200 pb-1 mb-2">Payments Received</h3>
            <table className="w-full text-xs">
              <tbody>
                {data.payments.map((p, i) => (
                  <tr key={i} className="border-b border-stone-100">
                    <td className="px-2 py-1.5 w-32">{fmtDate(p.date)}</td>
                    <td className="px-2 py-1.5 text-stone-600">{p.method || 'Cash'} {p.reference && <span className="text-[10px] text-stone-400"> · {p.reference}</span>}</td>
                    <td className="px-2 py-1.5 text-stone-500 text-[11px]">{p.notes}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums font-medium">{fmt(p.amount)}</td>
                  </tr>
                ))}
                <tr>
                  <td colSpan="3" className="px-2 py-1.5 font-medium text-right">Total received</td>
                  <td className="px-2 py-1.5 text-right font-semibold tabular-nums" data-testid="payments-subtotal">{fmt(data.payments_subtotal)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Returns & Refunds */}
        {data.returns && data.returns.length > 0 && (
          <div className="mt-8">
            <h3 className="text-sm font-semibold uppercase tracking-widest text-stone-600 border-b border-stone-200 pb-1 mb-2">Returns & Refunds</h3>
            <table className="w-full text-xs">
              <tbody>
                {data.returns.map((r, i) => (
                  <tr key={i} className="border-b border-stone-100">
                    <td className="px-2 py-1.5 w-32">{fmtDate(r.date)}</td>
                    <td className="px-2 py-1.5 text-stone-600">{r.method} {r.reference && <span className="text-[10px] text-stone-400"> · {r.reference}</span>}</td>
                    <td className="px-2 py-1.5 text-stone-500 text-[11px]">{r.notes}</td>
                    <td className={`px-2 py-1.5 text-right tabular-nums font-medium ${r.amount < 0 ? 'text-red-700' : ''}`}>{fmt(r.amount)}</td>
                  </tr>
                ))}
                <tr>
                  <td colSpan="3" className="px-2 py-1.5 font-medium text-right">Net returns credit</td>
                  <td className="px-2 py-1.5 text-right font-semibold tabular-nums" data-testid="returns-net">{fmt(data.returns_net)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Outstanding */}
        <div className={`mt-8 border-t-2 pt-4 max-w-md ml-auto ${data.outstanding > 0 ? 'border-amber-700' : 'border-lime-700'}`}>
          <div className={`flex justify-between font-bold text-lg ${data.outstanding > 0 ? 'text-amber-800' : 'text-lime-800'}`}>
            <span>{data.outstanding > 0 ? 'Outstanding balance' : 'Balance settled'}</span>
            <span className="tabular-nums" data-testid="outstanding">{fmt(data.outstanding)}</span>
          </div>
        </div>

        {/* Footer note */}
        <p className="mt-10 text-[10px] text-stone-500 border-t border-stone-200 pt-3">
          This statement is issued for your reference. All figures are inclusive of applicable taxes unless indicated. Please reach out for any clarifications.
        </p>
      </div>
    </div>
  );
}
